import { useState, type DragEvent, type ReactNode } from 'react';
import { RowMenu } from '../../components/RowMenu.js';
import { alignRect, stackOrder, type Alignment } from '../../lib/regionGeometry.js';
import type { SchemaIssue } from '../../lib/layoutSchema.js';
import type { LayoutRegion } from '../../lib/types.js';
import { DND_MODULE, DND_WIDGET } from './canvas.js';
import { WidgetActionEditor, WidgetOptions, moduleIcon, moduleLabel, widgetSummary } from './widgets.js';
import type { LayoutEditorState } from './useLayoutEditor.js';

/*
 * The editor's panel contents, shared by the desktop side panel and the
 * mobile bottom sheets. Each takes the editor state and renders a body only;
 * the frame (title, close/back buttons) belongs to the caller.
 */

export const regionTitle = (r: LayoutRegion, index: number): string => r.name || `Region ${index + 1}`;

export const ALIGNMENTS: { align: Exclude<Alignment, 'fill'>; label: string; icon: string }[] = [
  { align: 'left', label: 'Left', icon: '⇤' },
  { align: 'center', label: 'Centre', icon: '↔' },
  { align: 'right', label: 'Right', icon: '⇥' },
  { align: 'top', label: 'Top', icon: '⤒' },
  { align: 'middle', label: 'Middle', icon: '↕' },
  { align: 'bottom', label: 'Bottom', icon: '⤓' },
];

const CANVAS_PRESETS = [
  { label: 'Full HD landscape', width: 1920, height: 1080 },
  { label: 'Full HD portrait', width: 1080, height: 1920 },
  { label: '4K landscape', width: 3840, height: 2160 },
  { label: '4K portrait', width: 2160, height: 3840 },
  { label: 'HD 720p', width: 1280, height: 720 },
] as const;

/** Where a playlist row drag came from, carried in dataTransfer as JSON. */
interface WidgetDrag {
  ri: number;
  wi: number;
}

export function readWidgetDrag(data: DataTransfer): WidgetDrag | null {
  try {
    const parsed: unknown = JSON.parse(data.getData(DND_WIDGET));
    if (parsed && typeof parsed === 'object' && 'ri' in parsed && 'wi' in parsed) {
      const { ri, wi } = parsed as WidgetDrag;
      if (Number.isInteger(ri) && Number.isInteger(wi)) return { ri, wi };
    }
  } catch {
    // not ours
  }
  return null;
}

/**
 * A region's playlist as tappable rows with a ⋮ menu each. With `draggable`
 * (mouse), rows also reorder by drag, and can be dropped onto another region
 * on the canvas.
 */
export function PlaylistList({
  ed,
  ri,
  onOpen,
  onAdd,
  draggable = false,
}: {
  ed: LayoutEditorState;
  ri: number;
  onOpen: (widgetId: string) => void;
  onAdd: () => void;
  draggable?: boolean;
}): ReactNode {
  const [dropAt, setDropAt] = useState<number | null>(null);
  const doc = ed.doc;
  const items = doc?.regions[ri]?.playlist.items ?? [];
  if (!doc) return null;

  function over(e: DragEvent<HTMLElement>, index: number): void {
    if (!e.dataTransfer.types.includes(DND_WIDGET)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (dropAt !== index) setDropAt(index);
  }

  function drop(e: DragEvent<HTMLElement>): void {
    e.preventDefault();
    e.stopPropagation();
    const from = readWidgetDrag(e.dataTransfer);
    if (from && dropAt !== null) ed.moveWidget(from.ri, from.wi, ri, dropAt);
    setDropAt(null);
  }

  return (
    <>
      {items.length === 0 ? (
        <p
          className={`faint small m-empty${dropAt === 0 ? ' drop-active' : ''}`}
          onDragOver={draggable ? (e) => over(e, 0) : undefined}
          onDragLeave={() => setDropAt(null)}
          onDrop={draggable ? drop : undefined}
        >
          Nothing plays here yet.
        </p>
      ) : (
        <ul className="m-list" onDragLeave={(e) => !e.currentTarget.contains(e.relatedTarget as Node) && setDropAt(null)}>
          {items.map((w, i) => (
            <li
              key={w.id}
              className={`m-list-row${dropAt === i ? ' drop-before' : ''}${
                dropAt === items.length && i === items.length - 1 ? ' drop-after' : ''
              }`}
              draggable={draggable}
              onDragStart={
                draggable
                  ? (e) => {
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData(DND_WIDGET, JSON.stringify({ ri, wi: i }));
                    }
                  : undefined
              }
              onDragEnd={() => setDropAt(null)}
              onDragOver={
                draggable
                  ? (e) => {
                      const box = e.currentTarget.getBoundingClientRect();
                      over(e, e.clientY > box.top + box.height / 2 ? i + 1 : i);
                    }
                  : undefined
              }
              onDrop={draggable ? drop : undefined}
            >
              {draggable ? (
                <span className="drag-handle" aria-hidden title="Drag to reorder, or onto another region">
                  ⠿
                </span>
              ) : null}
              <button type="button" className="m-list-main" onClick={() => onOpen(w.id)}>
                <span className="m-list-icon" aria-hidden>
                  {moduleIcon(w.module)}
                </span>
                <span className="m-list-text">
                  <strong>{moduleLabel(w.module, ed.moduleBySlug.get(w.module))}</strong>
                  <span className="faint small ellipsis">{widgetSummary(w, ed.media, ed.menuBoards)}</span>
                </span>
                {w.duration ? <span className="faint small">{w.duration}s</span> : null}
              </button>
              <RowMenu
                label="Widget actions"
                items={[
                  { label: 'Move up', disabled: i === 0, onSelect: () => ed.moveWidgetBy(ri, i, -1) },
                  { label: 'Move down', disabled: i === items.length - 1, onSelect: () => ed.moveWidgetBy(ri, i, 1) },
                  ...doc.regions.map((other, oi) =>
                    oi === ri
                      ? null
                      : {
                          label: `Move to ${oi + 1} · ${regionTitle(other, oi)}`,
                          onSelect: () => ed.moveWidget(ri, i, oi, other.playlist.items.length),
                        },
                  ),
                  { label: 'Duplicate', onSelect: () => ed.duplicateWidget(ri, i) },
                  { label: 'Delete', danger: true, onSelect: () => ed.removeWidget(ri, i) },
                ]}
              />
            </li>
          ))}
        </ul>
      )}
      <button type="button" className="btn" onClick={onAdd}>
        ＋ Add widget
      </button>
    </>
  );
}

/** One widget's settings, full width: type, its options, its tap action. */
export function WidgetDetail({
  ed,
  ri,
  wi,
  onDeleted,
}: {
  ed: LayoutEditorState;
  ri: number;
  wi: number;
  onDeleted: () => void;
}): ReactNode {
  const widget = ed.doc?.regions[ri]?.playlist.items[wi];
  if (!ed.doc || !widget) return null;
  return (
    <>
      <label className="field">
        Type
        <select value={widget.module} onChange={(e) => ed.patchWidget(ri, wi, { module: e.target.value })}>
          {(ed.moduleChoices.includes(widget.module) ? ed.moduleChoices : [widget.module, ...ed.moduleChoices]).map((m) => (
            <option key={m} value={m}>
              {moduleLabel(m, ed.moduleBySlug.get(m))}
            </option>
          ))}
        </select>
      </label>
      <WidgetOptions
        widget={widget}
        media={ed.media}
        module={ed.moduleBySlug.get(widget.module)}
        menuBoards={ed.menuBoards}
        onPatch={(next, key) => ed.patchWidget(ri, wi, next, key)}
        stacked
      />
      <WidgetActionEditor
        widget={widget}
        layouts={ed.layouts}
        currentLayoutId={ed.doc.id}
        onPatch={(next, key) => ed.patchWidget(ri, wi, next, key)}
        stacked
      />
      <div className="row">
        <button type="button" className="btn" onClick={() => ed.duplicateWidget(ri, wi)}>
          Duplicate
        </button>
        <button
          type="button"
          className="btn danger"
          onClick={() => {
            ed.removeWidget(ri, wi);
            onDeleted();
          }}
        >
          Delete
        </button>
      </div>
    </>
  );
}

/**
 * The widget types as big tiles. Tapping one calls `onPick`; with
 * `draggable` (mouse) a tile can also be dropped onto any region on the canvas.
 */
export function WidgetTiles({
  ed,
  onPick,
  draggable = false,
}: {
  ed: LayoutEditorState;
  onPick?: (module: string) => void;
  draggable?: boolean;
}): ReactNode {
  return (
    <div className="m-tiles">
      {ed.moduleChoices.map((m) => {
        const module = ed.moduleBySlug.get(m);
        return (
          <button
            key={m}
            type="button"
            className="m-tile"
            title={module?.description ?? undefined}
            aria-disabled={!onPick}
            draggable={draggable}
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = 'copy';
              e.dataTransfer.setData(DND_MODULE, m);
            }}
            onClick={() => onPick?.(m)}
          >
            <span className="m-tile-icon" aria-hidden>
              {moduleIcon(m)}
            </span>
            <span className="ellipsis">{moduleLabel(m, module)}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Region name, exact rect, alignment and stacking for one region. */
export function PositionFields({ ed, ri }: { ed: LayoutEditorState; ri: number }): ReactNode {
  const doc = ed.doc;
  const region = doc?.regions[ri];
  if (!doc || !region) return null;
  const stack = stackOrder(doc.regions);
  const layer = stack.indexOf(region);
  const setRect = (rect: LayoutRegion['rect'], key?: string): void => ed.patchRegion(ri, { rect }, key);

  return (
    <>
      <label className="field">
        Region name
        <input
          value={region.name ?? ''}
          placeholder={`Region ${ri + 1}`}
          onChange={(e) => ed.patchRegion(ri, { name: e.target.value }, `name:${region.id}`)}
        />
      </label>
      <div className="m-grid-2">
        {(['x', 'y', 'width', 'height'] as const).map((k) => (
          <label key={k} className="field">
            {k === 'x' ? 'X' : k === 'y' ? 'Y' : k === 'width' ? 'Width' : 'Height'}
            <input
              type="number"
              inputMode="decimal"
              value={region.rect[k]}
              onChange={(e) => setRect({ ...region.rect, [k]: Number(e.target.value) || 0 }, `${k}:${region.id}`)}
            />
          </label>
        ))}
      </div>
      <div className="segmented m-full" role="group" aria-label="Unit">
        {(['px', 'percent'] as const).map((u) => (
          <button
            key={u}
            type="button"
            aria-pressed={(region.rect.unit ?? 'px') === u}
            onClick={() => setRect({ ...region.rect, unit: u })}
          >
            {u === 'px' ? 'Pixels' : 'Percent'}
          </button>
        ))}
      </div>
      <strong className="small">Align</strong>
      <div className="m-align">
        {ALIGNMENTS.map((a) => (
          <button
            key={a.align}
            type="button"
            className="btn small"
            onClick={() => setRect(alignRect(region.rect, a.align, doc.dimensions))}
          >
            <span aria-hidden>{a.icon}</span> {a.label}
          </button>
        ))}
      </div>
      <button type="button" className="btn" onClick={() => setRect(alignRect(region.rect, 'fill', doc.dimensions))}>
        Fill the whole screen
      </button>
      {stack.length > 1 ? (
        <>
          <strong className="small">
            Layer {layer + 1} of {stack.length}
          </strong>
          <div className="m-grid-2" role="group" aria-label="Layer">
            {(
              [
                ['backward', '↓ Backward', layer === 0],
                ['forward', '↑ Forward', layer === stack.length - 1],
                ['back', '⤓ To back', layer === 0],
                ['front', '⤒ To front', layer === stack.length - 1],
              ] as const
            ).map(([to, label, disabled]) => (
              <button
                key={to}
                type="button"
                className="btn small"
                disabled={disabled}
                onClick={() => ed.restackRegion(region.id, to)}
              >
                {label}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </>
  );
}

/** All regions, front-most first, with step-wise reordering. */
export function RegionsList({ ed, onSelect }: { ed: LayoutEditorState; onSelect: (id: string) => void }): ReactNode {
  const doc = ed.doc;
  if (!doc) return null;
  return (
    <>
      <p className="faint small" style={{ margin: 0 }}>
        Top of the list is in front.
      </p>
      <ul className="m-list">
        {[...stackOrder(doc.regions)].reverse().map((r, i, all) => {
          const index = doc.regions.indexOf(r);
          return (
            <li key={r.id} className={`m-list-row${r.id === ed.selectedRegion ? ' active' : ''}`}>
              <button type="button" className="m-list-main" onClick={() => onSelect(r.id)}>
                <span className="m-list-text">
                  <strong>{regionTitle(r, index)}</strong>
                  <span className="faint small">
                    {r.playlist.items.length} item{r.playlist.items.length === 1 ? '' : 's'}
                  </span>
                </span>
              </button>
              <button
                type="button"
                className="icon-btn"
                aria-label="Bring forward"
                disabled={i === 0}
                onClick={() => ed.restackRegion(r.id, 'forward')}
              >
                ↑
              </button>
              <button
                type="button"
                className="icon-btn"
                aria-label="Send backward"
                disabled={i === all.length - 1}
                onClick={() => ed.restackRegion(r.id, 'backward')}
              >
                ↓
              </button>
            </li>
          );
        })}
      </ul>
      <button type="button" className="btn" onClick={ed.addRegion}>
        ＋ Add region
      </button>
    </>
  );
}

/** Canvas orientation, common screen sizes and a custom size. */
export function CanvasSettings({ ed }: { ed: LayoutEditorState }): ReactNode {
  if (!ed.doc) return null;
  const { width, height } = ed.doc.dimensions;
  const orientation = width >= height ? 'landscape' : 'portrait';
  return (
    <>
      <div className="segmented m-full" role="group" aria-label="Orientation">
        {(['landscape', 'portrait'] as const).map((o) => (
          <button key={o} type="button" aria-pressed={orientation === o} onClick={() => ed.setOrientation(o)}>
            {o === 'landscape' ? 'Landscape' : 'Portrait'}
          </button>
        ))}
      </div>
      <div className="m-presets">
        {CANVAS_PRESETS.map((p) => (
          <button
            key={p.label}
            type="button"
            className="btn small"
            aria-pressed={p.width === width && p.height === height}
            onClick={() => ed.resizeTo(p.width, p.height)}
          >
            {p.label}
            <span className="faint">
              {' '}
              {p.width}×{p.height}
            </span>
          </button>
        ))}
      </div>
      <div className="m-grid-2">
        <label className="field">
          Width
          <input
            type="number"
            inputMode="numeric"
            value={width}
            onChange={(e) => ed.setCanvasSize(Number(e.target.value) || 1, height)}
          />
        </label>
        <label className="field">
          Height
          <input
            type="number"
            inputMode="numeric"
            value={height}
            onChange={(e) => ed.setCanvasSize(width, Number(e.target.value) || 1)}
          />
        </label>
      </div>
    </>
  );
}

export function checkTitle(issues: SchemaIssue[]): string {
  return issues.length === 0 ? 'Ready to publish' : `${issues.length} issue${issues.length === 1 ? '' : 's'} to fix`;
}

/** Every schema issue in the document, or a ready-to-publish note. */
export function IssueList({ issues }: { issues: SchemaIssue[] }): ReactNode {
  if (issues.length === 0) {
    return (
      <p className="muted small" style={{ margin: 0 }}>
        ✓ This layout matches the Layout Document schema.
      </p>
    );
  }
  return (
    <ul className="small form-error m-issues">
      {issues.map((iss, i) => (
        <li key={i}>
          <span className="mono">{iss.path}</span> {iss.message}
        </li>
      ))}
    </ul>
  );
}
